import { useState, useRef, useCallback, useEffect } from "react";
import { Play, Pause, Loader2, AlertCircle, Volume2, VolumeX, Maximize, Minimize, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";

declare global {
 interface Window {
 YT: any;
 onYouTubeIframeAPIReady: () => void;
 }
}

interface ImmersivePlayerProps {
 youtubeId: string;
 title?: string;
 autoplay?: boolean;
 className?: string;
 onTimeUpdate?: (currentTime: number) => void;
 onDurationChange?: (duration: number) => void;
 getCurrentTimeRef?: React.MutableRefObject<(() => number) | null>;
 startTime?: number;
 endTime?: number;
}

const ImmersivePlayer = ({
 youtubeId,
 title,
 autoplay = false,
 className = "",
 onTimeUpdate,
 onDurationChange,
 getCurrentTimeRef,
 startTime,
 endTime,
}: ImmersivePlayerProps) => {
 const [isPlaying, setIsPlaying] = useState(false);
 const [isLoading, setIsLoading] = useState(true);
 const [hasError, setHasError] = useState(false);
 const [showControls, setShowControls] = useState(true);
 const [isMuted, setIsMuted] = useState(false);
 const [isFullscreen, setIsFullscreen] = useState(false);
 const [currentTime, setCurrentTime] = useState(0);
 const [duration, setDuration] = useState(0);
 const [volume, setVolume] = useState(100);
 const [showSettings, setShowSettings] = useState(false);
 const [qualities, setQualities] = useState<string[]>([]);
 const [currentQuality, setCurrentQuality] = useState<string>("auto");

 const playerContainerRef = useRef<HTMLDivElement>(null);
 const playerRef = useRef<any>(null);
 const containerRef = useRef<HTMLDivElement>(null);
 const hideControlsTimer = useRef<ReturnType<typeof setTimeout>>();
 const timeUpdateInterval = useRef<ReturnType<typeof setInterval>>();

 useEffect(() => {
 // Load YouTube API script if not loaded
 if (!window.YT) {
 const tag = document.createElement("script");
 tag.src = "https://www.youtube.com/iframe_api";
 const firstScriptTag = document.getElementsByTagName("script")[0];
 if (firstScriptTag?.parentNode) {
 firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
 }

 window.onYouTubeIframeAPIReady = initPlayer;
 } else if (window.YT.Player) {
 initPlayer();
 }

 return () => {
 clearInterval(timeUpdateInterval.current);
 if (playerRef.current) {
 playerRef.current.destroy();
 }
 };
 }, [youtubeId, startTime, endTime]); // Re-init if youtubeId or times change

 const initPlayer = () => {
 if (!playerContainerRef.current) return;
 
 // Destroy previous instance if exists
 if (playerRef.current) {
 playerRef.current.destroy();
 }

 playerRef.current = new window.YT.Player(playerContainerRef.current, {
 videoId: youtubeId,
 playerVars: {
 autoplay: autoplay ? 1 : 0,
 controls: 0,
 modestbranding: 1,
 rel: 0,
 fs: 0,
 disablekb: 1,
 playsinline: 1,
 iv_load_policy: 3,
 showinfo: 0,
 cc_load_policy: 0,
 origin: window.location.origin,
 start: startTime ? Math.floor(startTime) : undefined,
 end: endTime ? Math.floor(endTime) : undefined,
 },
 events: {
 onReady: onPlayerReady,
 onStateChange: onPlayerStateChange,
 onPlaybackQualityChange: onQualityChange,
 onError: onPlayerError,
 },
 });
 };

 const onPlayerReady = (event: any) => {
 setIsLoading(false);
 
 const initialDuration = event.target.getDuration() || 0;
 setDuration(initialDuration);
 onDurationChange?.(initialDuration);

 const availableQualities = event.target.getAvailableQualityLevels();
 if (availableQualities && availableQualities.length > 0) {
 setQualities(availableQualities);
 }
 
 if (autoplay) {
 event.target.playVideo();
 } else {
 event.target.pauseVideo();
 }
 
 resetHideTimer();
 };

 const onPlayerStateChange = (event: any) => {
 if (event.data === window.YT.PlayerState.PLAYING) {
 setIsPlaying(true);
 resetHideTimer();
 
 // Update duration just in case it wasn't ready
 const currentDur = playerRef.current?.getDuration() || 0;
 if (currentDur > 0 && currentDur !== duration) {
 setDuration(currentDur);
 onDurationChange?.(currentDur);
 }
 
 // Start polling for current time with high precision (100ms)
 clearInterval(timeUpdateInterval.current);
 timeUpdateInterval.current = setInterval(() => {
 if (playerRef.current && playerRef.current.getCurrentTime) {
 const time = playerRef.current.getCurrentTime();
 setCurrentTime(time);
 onTimeUpdate?.(time);
 
 // Refetch duration to ensure accuracy
 const d = playerRef.current.getDuration();
 if (d > 0 && d !== duration) {
 setDuration(d);
 onDurationChange?.(d);
 }
 }
 }, 100);
 
 } else {
 setIsPlaying(false);
 setShowControls(true);
 clearInterval(timeUpdateInterval.current);
 }
 };

 const onQualityChange = (event: any) => {
 setCurrentQuality(event.data);
 };

 const onPlayerError = () => {
 setIsLoading(false);
 setHasError(true);
 };

 // Expose getCurrentTime via ref
 useEffect(() => {
 if (getCurrentTimeRef) {
 getCurrentTimeRef.current = () => currentTime;
 }
 }, [getCurrentTimeRef, currentTime]);

 const resetHideTimer = useCallback(() => {
 clearTimeout(hideControlsTimer.current);
 setShowControls(true);
 hideControlsTimer.current = setTimeout(() => {
 // Only hide controls if playing and not viewing settings
 if (playerRef.current?.getPlayerState() === window.YT.PlayerState.PLAYING && !showSettings) {
 setShowControls(false);
 }
 }, 3000);
 }, [showSettings]);

 const handlePlayPause = useCallback(() => {
 if (!playerRef.current) return;
 
 if (isPlaying) {
 playerRef.current.pauseVideo();
 } else {
 playerRef.current.playVideo();
 }
 }, [isPlaying]);

 const handleOverlayClick = useCallback(() => {
 if (showSettings) {
 setShowSettings(false);
 resetHideTimer();
 return;
 }
 
 if (isPlaying) {
 resetHideTimer();
 } else {
 handlePlayPause();
 }
 }, [isPlaying, handlePlayPause, resetHideTimer, showSettings]);

 const handleVolumeToggle = useCallback(() => {
 if (!playerRef.current) return;
 
 if (isMuted) {
 playerRef.current.unMute();
 setIsMuted(false);
 // Sync real volume to UI
 const realVol = playerRef.current.getVolume();
 setVolume(realVol);
 } else {
 playerRef.current.mute();
 setIsMuted(true);
 setVolume(0);
 }
 }, [isMuted]);

 const handleVolumeChange = useCallback((value: number[]) => {
 if (!playerRef.current) return;
 const vol = value[0];
 
 playerRef.current.setVolume(vol);
 setVolume(vol);
 
 if (vol === 0) {
 playerRef.current.mute();
 setIsMuted(true);
 } else if (isMuted) {
 playerRef.current.unMute();
 setIsMuted(false);
 }
 }, [isMuted]);

 const handleSeek = useCallback((value: number[]) => {
 if (!playerRef.current) return;
 const time = value[0];
 playerRef.current.seekTo(time, true);
 setCurrentTime(time);
 }, []);

 const handleQualitySelect = (quality: string) => {
 if (playerRef.current) {
 playerRef.current.setPlaybackQuality(quality);
 setCurrentQuality(quality);
 }
 setShowSettings(false);
 resetHideTimer();
 };

 const handleFullscreen = useCallback(() => {
 if (!containerRef.current) return;
 if (!document.fullscreenElement) {
 containerRef.current.requestFullscreen?.();
 setIsFullscreen(true);
 } else {
 document.exitFullscreen?.();
 setIsFullscreen(false);
 }
 }, []);

 useEffect(() => {
 const handleFsChange = () => {
 setIsFullscreen(!!document.fullscreenElement);
 };
 document.addEventListener("fullscreenchange", handleFsChange);
 return () => document.removeEventListener("fullscreenchange", handleFsChange);
 }, []);

 const formatTime = (seconds: number) => {
 if (isNaN(seconds)) return "0:00";
 const h = Math.floor(seconds / 3600);
 const m = Math.floor((seconds % 3600) / 60);
 const s = Math.floor(seconds % 60);
 
 if (h > 0) {
 return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
 }
 return `${m}:${s.toString().padStart(2, "0")}`;
 };

 if (!youtubeId) return null;

 return (
 <div
 ref={containerRef}
 className={`relative w-full h-full rounded-xl overflow-hidden bg-background group ${className}`}
 onMouseMove={resetHideTimer}
 onMouseLeave={() => {
 if (isPlaying && !showSettings) setShowControls(false);
 }}
 >
 {/* Target for YT.Player injection */}
 <div className="absolute inset-0 w-full h-full pointer-events-none">
 <div ref={playerContainerRef} className="w-full h-full" />
 </div>

 {/* Click interceptor overlay */}
 <div
 className="absolute inset-0 z-10 cursor-pointer"
 onClick={handleOverlayClick}
 />

 {/* Loading */}
 <AnimatePresence>
 {isLoading && (
 <motion.div
 initial={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-background"
 >
 <Loader2 className="w-10 h-10 text-primary animate-spin mb-3" />
 <p className="text-muted-foreground text-sm">Carregando vídeo…</p>
 </motion.div>
 )}
 </AnimatePresence>

 {/* Error */}
 {hasError && !isLoading && (
 <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-foreground/10 gap-3">
 <AlertCircle className="w-10 h-10 text-destructive" />
 <p className="text-foreground text-sm font-semibold">Não foi possível carregar o vídeo</p>
 <p className="text-muted-foreground text-xs">Verifique sua conexão e tente novamente</p>
 </div>
 )}

 {/* Custom controls */}
 <AnimatePresence>
 {showControls && !isLoading && !hasError && (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.2 }}
 className="absolute inset-0 z-20 pointer-events-none flex flex-col justify-between"
 >
 {/* Top gradient for title */}
 <div className="w-full h-24 bg-gradient-to-b from-black/80 to-transparent absolute top-0 pointer-events-none" />
 
 {/* Title */}
 {title && (
 <div className="absolute top-0 left-0 right-0 px-4 py-3 pointer-events-none z-10">
 <p className="text-foreground text-sm md:text-base font-semibold truncate drop-shadow-md">{title}</p>
 </div>
 )}

 {/* Bottom gradient */}
 <div className="w-full h-32 bg-gradient-to-t from-black/90 via-black/40 to-transparent absolute bottom-0 pointer-events-none" />

 {/* Center play/pause */}
 <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
 <motion.button
 whileHover={{ scale: 1.1 }}
 whileTap={{ scale: 0.9 }}
 onClick={(e) => { e.stopPropagation(); handlePlayPause(); }}
 className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center shadow-lg shadow-primary/30 transition-colors hover:bg-primary pointer-events-auto"
 >
 {isPlaying ? (
 <Pause className="w-7 h-7 md:w-8 md:h-8 text-primary-foreground" />
 ) : (
 <Play className="w-7 h-7 md:w-8 md:h-8 text-primary-foreground ml-1" />
 )}
 </motion.button>
 </div>

 {/* Settings Menu Popup */}
 <AnimatePresence>
 {showSettings && (
 <motion.div 
 initial={{ opacity: 0, y: 10, scale: 0.95 }}
 animate={{ opacity: 1, y: 0, scale: 1 }}
 exit={{ opacity: 0, scale: 0.95 }}
 className="absolute bottom-16 right-4 bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl p-2 z-30 pointer-events-auto w-48 max-h-48 overflow-y-auto custom-scrollbar"
 >
 <p className="text-xs font-semibold text-muted-foreground mb-2 px-2 pt-1 border-b border-zinc-800 pb-2">Qualidade de Vídeo</p>
 <div className="space-y-1">
 {qualities.length > 0 ? qualities.map((q) => (
 <button
 key={q}
 onClick={(e) => { e.stopPropagation(); handleQualitySelect(q); }}
 className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${currentQuality === q ? 'bg-primary text-primary-foreground' : 'text-zinc-300 hover:bg-zinc-800'}`}
 >
 {q === 'auto' ? 'Automático' : q === 'hd1080' ? '1080p' : q === 'hd720' ? '720p' : q === 'large' ? '480p' : q === 'medium' ? '360p' : q === 'small' ? '240p' : q === 'tiny' ? '144p' : q}
 </button>
 )) : (
 <p className="text-xs text-zinc-500 px-2 py-1">Indisponível</p>
 )}
 </div>
 </motion.div>
 )}
 </AnimatePresence>

 {/* Bottom controls bar */}
 <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8 pointer-events-auto z-20">
 {/* Progress bar */}
 <div className="mb-3 group/slider flex items-center h-4 cursor-pointer">
 <Slider
 value={[currentTime]}
 min={0}
 max={duration || 100}
 step={0.5}
 onValueChange={handleSeek}
 className="w-full h-1.5 bg-zinc-800 rounded-full cursor-pointer hover:h-2 transition-all [&_[role=slider]]:h-3 [&_[role=slider]]:w-3 [&_[role=slider]]:bg-primary [&_[role=slider]]:opacity-0 group-hover/slider:[&_[role=slider]]:opacity-100"
 />
 </div>
 
 <div className="flex items-center justify-between gap-4">
 <div className="flex items-center gap-3 md:gap-4">
 <button onClick={(e) => { e.stopPropagation(); handlePlayPause(); }} className="text-foreground hover:text-primary transition-colors">
 {isPlaying ? <Pause className="w-5 h-5 md:w-6 md:h-6" /> : <Play className="w-5 h-5 md:w-6 md:h-6" />}
 </button>
 
 <div className="flex items-center gap-2 group/volume relative">
 <button onClick={(e) => { e.stopPropagation(); handleVolumeToggle(); }} className="text-foreground hover:text-primary transition-colors">
 {isMuted || volume === 0 ? <VolumeX className="w-5 h-5 md:w-6 md:h-6" /> : <Volume2 className="w-5 h-5 md:w-6 md:h-6" />}
 </button>
 <div className="w-0 overflow-hidden group-hover/volume:w-20 transition-all duration-300 ease-in-out md:w-20 hidden sm:block">
 <Slider
 value={[isMuted ? 0 : volume]}
 min={0}
 max={100}
 step={1}
 onValueChange={handleVolumeChange}
 className="[&_[role=slider]]:h-3 [&_[role=slider]]:w-3 [&_[role=slider]]:bg-white h-1.5 cursor-pointer"
 />
 </div>
 </div>

 <span className="text-xs md:text-sm text-foreground/40 font-mono font-medium">
 {formatTime(currentTime)} <span className="text-foreground/40 mx-1">/</span> {formatTime(duration)}
 </span>
 </div>

 <div className="flex items-center gap-3">
 <button 
 onClick={(e) => { 
 e.stopPropagation(); 
 setShowSettings(!showSettings); 
 resetHideTimer();
 }} 
 className={`transition-colors ${showSettings || currentQuality !== 'auto' ? 'text-primary' : 'text-foreground hover:text-primary'}`}
 title="Qualidade do Vídeo"
 >
 <Settings className={`w-5 h-5 md:w-6 md:h-6 ${showSettings ? 'animate-spin-slow' : ''}`} style={{ animationDuration: '4s' }} />
 </button>
 <button onClick={(e) => { e.stopPropagation(); handleFullscreen(); }} className="text-foreground hover:text-primary transition-colors">
 {isFullscreen ? <Minimize className="w-5 h-5 md:w-6 md:h-6" /> : <Maximize className="w-5 h-5 md:w-6 md:h-6" />}
 </button>
 </div>
 </div>
 </div>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 );
};

export default ImmersivePlayer;
